import * as React from 'react';

import { ValidationFields, validateForm } from './util';

export { isValidEmail, isValidUrl } from './util';

export interface FormProps<TFormData extends { [key: string]: unknown }> {
  onFormChange?(form: FormState<TFormData>['form']): void;
  onSubmit(form: FormState<TFormData>['form']): Promise<void> | void;
  initialValues: FormState<TFormData>['form'];
  validateFields?: ValidationFields<TFormData>;
  children({
    form,
    errors,
    onChange,
    onSubmit,
    isSubmittingForm,
  }: {
    form: FormState<TFormData>['form'];
    errors: FormState<TFormData>['errors'];
    onChange: (e: handeInputChangeEvent) => void;
    onSubmit: (e: handleFormSubmitEvent) => Promise<void> | void;
    isSubmittingForm: boolean;
  }): React.ReactNode;
}

export interface FormState<TFormData> {
  form: TFormData;
  errors: { [key in keyof TFormData]?: string };
  isSubmittingForm: boolean;
}

interface ValueEvent {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any;
  name: string;
}
export type handeInputChangeEvent =
  | React.SyntheticEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  | ValueEvent;
export type handleFormSubmitEvent = React.SyntheticEvent<HTMLFormElement | HTMLButtonElement>;

function isValueEvent(value: handeInputChangeEvent): value is ValueEvent {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (value as any).name !== undefined && (value as any).value !== undefined;
}

export class Form<TFormData extends { [key: string]: unknown }> extends React.Component<
  FormProps<TFormData>,
  FormState<TFormData>
> {
  constructor(props: FormProps<TFormData>) {
    super(props);

    this.state = { form: Object.assign({}, this.props.initialValues), isSubmittingForm: false, errors: {} };
  }

  handleFormSubmit = async (e: handleFormSubmitEvent) => {
    e.preventDefault();
    const { errors, isFormValid } = validateForm(this.state.form, this.props.validateFields);

    if (isFormValid) {
      this.setState({ isSubmittingForm: true });
      try {
        await Promise.resolve(this.props.onSubmit(this.state.form));
      } finally {
        this.setState({ isSubmittingForm: false });
      }
    }

    this.setState({ errors });
  };

  updateFormState = (name: string, value: string | number | boolean) => {
    const updatedForm = Object.assign(this.state.form, { [name]: value });

    if (this.props.onFormChange !== undefined) {
      this.props.onFormChange(updatedForm);
    }

    this.setState({
      form: updatedForm,
      errors: Object.assign(this.state.errors, { [name]: undefined }),
    });
  };

  handleInputNumberChange = (name: string, value: string) => {
    const valueNr = Number(value);
    if (isNaN(valueNr)) {
      return;
    }
    return this.updateFormState(name, valueNr);
  };

  handleChange = (e: handeInputChangeEvent) => {
    if (isValueEvent(e)) {
      return this.updateFormState(e.name, e.value);
    }

    const { currentTarget } = e;

    if (currentTarget instanceof HTMLInputElement && currentTarget.type === 'checkbox') {
      return this.updateFormState(currentTarget.name, currentTarget.checked);
    }

    if (currentTarget instanceof HTMLInputElement && currentTarget.type === 'number') {
      return this.handleInputNumberChange(currentTarget.name, currentTarget.value);
    }

    return this.updateFormState(currentTarget.name, currentTarget.value);
  };

  render() {
    if (!this.props.children) {
      return null;
    }

    return this.props.children({
      form: this.state.form,
      errors: this.state.errors,
      onChange: this.handleChange,
      onSubmit: this.handleFormSubmit,
      isSubmittingForm: this.state.isSubmittingForm,
    });
  }
}
